import { useEffect, useState } from 'react';
import { map, find, forEach, isEqual, omit, cloneDeep } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Button from 'components/form/Button';
import TextArea from 'components/form/TextArea';
import Dropdown from 'components/form/Dropdown';
import TextInput from 'components/form/TextInput';
import TimePicker from 'components/form/TimePicker';
import FileUpload from 'components/form/FileUpload';
import MultipleSelect from 'components/form/MultipleSelect';
import Modal from 'components/Layout/Modal';
import {
  addAgenda,
  getEvent,
  fetchDetailedViewData,
  throwError,
} from 'store/slices';
import {
  getFilenameFromUrl,
  objectToFormData,
  titleCaseString,
} from 'utils/helpers';
import {
  participationMode,
  speakerCategoryOptions,
  limit,
} from 'utils/constants';
import moment from 'moment';
import SessionTimeRange from 'components/form/SessionTimeRange';

const SessionForm = ({
  onHide,
  sessionId,
  handelSuccess,
  displayList,
  fetchData,
}) => {
  const alreadyAddedSession = displayList?.filter(
    (o) => o?.agenda_sessions?.length > 0
  );

  const sessionTimesList = alreadyAddedSession?.flatMap((o) =>
    o?.agenda_sessions?.map((session) => ({
      date: session?.date,
      time: session?.session_time,
      id: session?.id,
    }))
  );

  const params = useParams();
  const dispatch = useDispatch();
  const { eventName, eventAgendas, speakerDetails } = useSelector((state) => ({
    eventName: state.global.eventData.event_name,
    eventAgendas: state.global.eventData.event_agendas,
    speakerDetails: state.global.eventData.speaker_details,
  }));

  const newSpeakerDetails = speakerDetails?.map((o) => {
    return {
      ...o,
      sName: o?.name || o?.new_user_name,
    };
  });

  const [btnLoading, setBtnLoading] = useState(false);
  const [initialValues, setInitialValues] = useState({
    id: '',
    session_time: '',
    session_end_time: '',
    session_room_name: '',
    session_topic: '',
    name: '',
    date: '',
    description: '',
    speaker_id: '',
    speaker_category: '',
    participation_mode: '',
    transcription: '',
    transcriptionFileName: '',
  });

  const fetchEventDetails = async () => {
    await dispatch(getEvent(params?.eventId));
  };
  const handelSave = async (values) => {
    const formValue = omit(values, 'transcriptionFileName');
    setBtnLoading(true);
    let findAgenda = find(eventAgendas, (o) => o.date === formValue.date);
    if (sessionId && sessionId !== '+1') {
      findAgenda = {
        event_id: params?.eventId,
        ...findAgenda,
        agenda_sessions: JSON.stringify(
          map(findAgenda.agenda_sessions, (o) => {
            if (o.id === sessionId) {
              return formValue;
            } else {
              return o;
            }
          })
        ),
      };
    } else {
      findAgenda = {
        ...findAgenda,
        event_id: params?.eventId,
        date: formValue?.date,
        agenda_sessions: JSON.stringify([
          ...findAgenda.agenda_sessions,
          { ...formValue },
        ]),
      };
    }
    const payload = objectToFormData(findAgenda);
    const response = await dispatch(addAgenda(payload));
    if (response?.status === 200) {
      fetchEventDetails();
      if (handelSuccess) {
        handelSuccess();
      }
      onHide();
      fetchData();
    }
    setBtnLoading(false);
  };

  useEffect(() => {
    if (sessionId && sessionId !== '+1') {
      let findObject = {};
      forEach(eventAgendas, (o) =>
        forEach(o.agenda_sessions, (y) => {
          if (y.id === sessionId) {
            findObject = y;
          }
        })
      );
      setInitialValues(findObject);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, eventAgendas]);

  useEffect(() => {
    if (!eventAgendas) {
      fetchEventDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [recieverList, setRecieverList] = useState({
    list: [],
    name: '',
    total: 0,
    offset: 0,
    limit: limit,
    isLoading: true,
  });
  const [timer, setTimer] = useState('');

  const getUserEmailList = async (data = recieverList, isReset) => {
    const { name, offset, limit } = data;
    const formData = objectToFormData({
      event_id: params.eventId,
      offset,
      limit,
      author_email: name,
    });

    const response = await dispatch(fetchDetailedViewData(formData));
    if (response.status === 200) {
      const newList =
        response?.data?.abstracts?.map((abstract) => ({
          email_id: abstract?.authorEmail || '',
          paper_title: abstract?.paperTitle || '',
          author_name: abstract?.authorName || '',
          participation_mode: abstract?.presentation_type || '',
          abstract_id: abstract?.id || ''
        })) || [];

      setRecieverList((prev) => {
        let resData = newList;
        let listData = isReset ? resData : [...prev.list, ...resData];
        return {
          ...prev,
          list: listData,
          total: response?.data?.result_count || 0,
          isLoading: false,
        };
      });
    }
  };

  const handleSearchUser = (e) => {
    let time = timer;
    clearTimeout(time);
    time = setTimeout(() => {
      let oldData = cloneDeep({
        ...recieverList,
        offset: 0,
        name: e?.toLowerCase(),
        isLoading: true,
      });
      setRecieverList(oldData);
      getUserEmailList(oldData, true);
    }, 800);
    setTimer(time);
  };

  const handelAgendaEmailScroll = () => {
    if (recieverList.list.length < recieverList.total) {
      let oldData = cloneDeep({
        ...recieverList,
        offset: recieverList.offset + limit,
        isLoading: true,
      });
      setRecieverList(oldData);
      getUserEmailList(oldData);
    }
  };

  useEffect(() => {
    getUserEmailList();
  }, []);

  // const validationSchema = Yup.object().shape({
  //   name: Yup.string().required('Name is required.'),
  //   session_topic: Yup.string().required('Session topic is required.'),
  //   session_time: Yup.string().required('Session start time is required.'),
  //   session_end_time: Yup.string()
  //     .nullable()
  //     .required('Session end time is required.')
  //     .test(
  //       'is-after-start',
  //       'End time must be after start time.',
  //       function (value) {
  //         const { session_time } = this.parent;
  //         if (!session_time || !value) return true;

  //         // Compare time using moment
  //         const start = moment(session_time, 'HH:mm');
  //         const end = moment(value, 'HH:mm');

  //         return end.isAfter(start);
  //       }
  //     ),
  //   date: Yup.string().required('Date is required.'),
  //   description: Yup.string().required('Description is required.'),
  //   // .max(100, 'Maximum 100 character allow for this field.'),
  //   speaker_id: Yup.string().required('Speakers is required.'),
  //   speaker_category: Yup.string().required('Speaker category is required.'),
  //   participation_mode: Yup.string().required(
  //     'Participation mode is required.'
  //   ),
  //   transcription: Yup.string().required('Transcription is required.'),
  //   session_email_id: Yup.string().when('speaker_category', {
  //     is: '9',
  //     then: Yup.string().required('Email Id is required.'),
  //   }),
  //   session_room_name:Yup.string().required('Session Room required.'),
  // });

  const validationSchema = Yup.object().shape({
    speaker_category: Yup.string().required('Speaker category is required.'),
    session_email_id: Yup.string().when(['speaker_category'], {
      is: (speaker_category) => speaker_category == '9',
      then: (schema) => schema.required('Email Id is required.'),
      otherwise: (schema) => schema.notRequired(),
    }),

    name: Yup.string().when(['speaker_category'], {
      is: (speaker_category) => speaker_category != '9',
      then: (schema) => schema.required('Name is required.'),
      otherwise: (schema) => schema.notRequired(),
    }),

    session_topic: Yup.string().required('Session topic is required.'),

    session_time: Yup.string()
      .when(['speaker_category'], {
        is: (speaker_category) => speaker_category != '9',
        then: (schema) => schema.required('Session start time is required.'),
        otherwise: (schema) => schema.notRequired(),
      })
      .test(
        'is-overlap',
        'Session time overlaps with another session.',
        function (value) {
          const { session_end_time, date } = this.parent;
          // Skip overlap check if no values provided
          if (!value || !session_end_time || !date) return true;

          const currentStart = moment(value, 'HH:mm');
          const currentEnd = moment(session_end_time, 'HH:mm');

          if (!currentStart.isValid() || !currentEnd.isValid()) return true;

          // Find existing sessions for the selected date
          const dayAgenda = displayList?.find((d) => d.date === date);
          const existingSessions = dayAgenda?.agenda_sessions || [];

          const hasOverlap = existingSessions.some((session) => {
            // Skip the current session if we are editing
            if (String(session.id) === String(sessionId)) return false;

            const otherStart = moment(session.session_time, 'HH:mm');
            const otherEnd = moment(session.session_end_time, 'HH:mm');

            if (!otherStart.isValid() || !otherEnd.isValid()) return false;

            return (
              currentStart.isBefore(otherEnd) &&
              currentEnd.isAfter(otherStart)
            );
          });

          return !hasOverlap;
        }
      ),

    session_end_time: Yup.string()
      .nullable()
      .when(['speaker_category'], {
        is: (speaker_category) => speaker_category != '9',
        then: (schema) => schema.required('Session end time is required.'),
        otherwise: (schema) => schema.notRequired(),
      })
      .test(
        'is-after-start',
        'End Time must be greater than Start Time.',
        function (value) {
          const { session_time } = this.parent;
          if (!session_time || !value) return true;

          const start = moment(session_time, 'HH:mm');
          const end = moment(value, 'HH:mm');
          return end.isAfter(start);
        }
      )
      .test(
        'is-overlap-end',
        'Session time overlaps with another session.',
        function (value) {
          const { session_time, date } = this.parent;
          // Skip overlap check if no values provided
          if (!session_time || !value || !date) return true;

          const currentStart = moment(session_time, 'HH:mm');
          const currentEnd = moment(value, 'HH:mm');

          if (!currentStart.isValid() || !currentEnd.isValid())
            return true;

          const dayAgenda = displayList?.find((d) => d.date === date);
          const existingSessions = dayAgenda?.agenda_sessions || [];

          const hasOverlap = existingSessions.some((session) => {
            if (String(session.id) === String(sessionId)) return false;

            const otherStart = moment(session.session_time, 'HH:mm');
            const otherEnd = moment(session.session_end_time, 'HH:mm');

            if (!otherStart.isValid() || !otherEnd.isValid())
              return false;

            return (
              currentStart.isBefore(otherEnd) &&
              currentEnd.isAfter(otherStart)
            );
          });

          return !hasOverlap;
        }
      ),

    // date: Yup.string().when('speaker_category', {
    //   is: (val) => val !== '9',
    //   then: (schema) => schema.required('Date is required.'),
    //   otherwise: (schema) => schema.notRequired(),
    // }),
    date: Yup.string().required('Date is required.'),

    description: Yup.string().when(['speaker_category'], {
      is: (speaker_category) => speaker_category != '9',
      then: (schema) => schema.required('Description is required.'),
      otherwise: (schema) => schema.notRequired(),
    }),

    speaker_id: Yup.mixed().when(['speaker_category'], {
      is: (val) => val == '9',
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required('Speakers is required.'),
    }),

    participation_mode: Yup.string().required(
      'Participation mode is required.'
    ),

    // transcription: Yup.string().when('speaker_category', {
    //   is: (val) => val !== '9',
    //   then: (schema) => schema.required('Transcription is required.'),
    //   otherwise: (schema) => schema.notRequired(),
    // }),

    session_room_name: Yup.string().when(['speaker_category'], {
      is: (speaker_category) => speaker_category != '9',
      then: (schema) => schema.required('Session Room required.'),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  return (
    <Modal
      onHide={onHide}
      title={sessionId && sessionId !== '+1' ? 'Edit Session' : 'Add Session'}
    // size="md"
    >
      {/* <Card className="d-flex align-items-center unset-br cmt-20 cpe-26 cps-26 cpt-30 cpb-30"> */}
      <div className="cmt-20 cpe-26 cps-26 col-md-12 col-10">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handelSave}
        >
          {(props) => {
            const {
              values,
              errors,
              setFieldValue,
              handleChange,
              handleSubmit,
            } = props;
            return (
              <form>
                <div className="row">
                  <div className="col-md-12 col-12 cmb-22">
                    <TextInput
                      label="Event Name"
                      placeholder="Event Name"
                      onChange={() => { }}
                      value={eventName}
                      disabled
                    />
                  </div>

                  {values?.speaker_category == '9' && (
                    <div className="col-md-12 col-12 cmb-22">
                      <Dropdown
                        label="Email Id"
                        id="session_email_id"
                        options={recieverList?.list}
                        optionKey="email_id"
                        optionValue="email_id"
                        onMenuScrollToBottom={() => handelAgendaEmailScroll()}
                        onInputChange={handleSearchUser}
                        onChange={(e) => {
                          const selectedEmail = e.target.value;
                          const relatedData = recieverList?.list?.find(
                            (item) => item?.email_id === selectedEmail
                          );

                          if (relatedData) {
                            props.setValues({
                              ...values,
                              session_email_id: selectedEmail,
                              name:
                                values?.speaker_category == '9'
                                  ? relatedData?.author_name
                                  : relatedData?.paper_title,
                              session_topic: relatedData?.paper_title || '',
                              abstract_id: relatedData?.abstract_id || '',
                              participation_mode:
                                relatedData?.participation_mode === 'Physical'
                                  ? '1'
                                  : '2',
                            });
                          } else {
                            setFieldValue('session_email_id', selectedEmail);
                            setFieldValue('name', '');
                            setFieldValue('session_topic', '');
                            setFieldValue('participation_mode', '');
                          }
                        }}
                        placeholder="Select Email Id"
                        isLoading={recieverList?.isLoading}
                        value={values.session_email_id}
                        error={errors.session_email_id}
                      />
                    </div>
                  )}

                  <div className="col-md-12 col-12 cmb-22">
                    <TextInput
                      label="Room Name"
                      placeholder="Enter Room Name"
                      id="session_room_name"
                      onChange={(e) => {
                        const id = e.target.id;
                        const value = titleCaseString(e.target.value);
                        setFieldValue(id, value);
                      }}
                      value={values.session_room_name}
                      error={errors.session_room_name}
                    />
                  </div>
                  <div className="col-md-12">
                    <div className="row">
                      <div className="col-md-12 col-12 cmb-22">
                        <Dropdown
                          label="Date"
                          id="date"
                          placeholder="Select Date"
                          value={values.date}
                          error={errors.date}
                          options={eventAgendas || []}
                          optionKey="date"
                          optionValue="date"
                          onChange={(e) => {
                            handleChange(e);
                            setFieldValue('session_time', '');
                            setFieldValue('session_end_time', '');
                          }}
                          disabled={sessionId && sessionId !== '+1'}
                        />
                      </div>
                      {/* <div className="col-md-4 col-12 cmb-22">
                        <TimePicker
                          label="Start Time"
                          placeholder="Enter Session Time"
                          id="session_time"
                          onChange={(e) => {
                            const id = e.target.id;
                            const value = e.target.value;
                            const formattedTime = moment(value, 'HH:mm').format(
                              'HH:mm:ss'
                            );
                            const selectedDate = values?.date;

                            const errMsg = {
                              message: `${moment(value, 'HH:mm').format(
                                'hh:mm A'
                              )} is already taken.`,
                            };

                            const isTimeTaken = sessionTimesList?.some(
                              (session) =>
                                session?.date === selectedDate &&
                                session?.time === formattedTime &&
                                session?.id !== sessionId
                            );

                            if (isTimeTaken) {
                              dispatch(throwError(errMsg));
                              setFieldValue(id, '');
                            } else {
                              setFieldValue(id, value);
                            }
                          }}
                          value={values.session_time}
                          error={errors.session_time}
                        />
                      </div>

                      <div className="col-md-4 col-12 cmb-22">
                        <TimePicker
                          label="End Time"
                          placeholder="Enter Session Time"
                          id="session_end_time"
                          onChange={(e) => {
                            const id = e.target.id;
                            const value = e.target.value;
                            setFieldValue(id, value);
                          }}
                          value={values.session_end_time}
                          error={errors.session_end_time}
                        />
                      </div> */}
                      <div className="col-12 cmb-22">
                        <SessionTimeRange
                          startLabel="Session Start"
                          endLabel="Session End"
                          durationLabel="Selected"
                          startId={`session_time`}
                          endId={`session_end_time`}
                          startValue={values.session_time}
                          endValue={values.session_end_time}
                          startError={errors.session_time}
                          endError={errors.session_end_time}
                          onDurationChange={(value) =>
                            setFieldValue(
                              `duration`,
                              value
                            )
                          }
                          onStartChange={(value) => {
                            if (!value) {
                              setFieldValue('session_time', '');
                              return;
                            }
                            const formattedTime = moment(value, 'HH:mm').format(
                              'HH:mm:ss'
                            );
                            // const selectedDate = values?.date;

                            // const errMsg = {
                            //   message: `${moment(value, 'HH:mm').format(
                            //     'hh:mm A'
                            //   )} is already taken.`,
                            // };

                            // const isTimeTaken = sessionTimesList?.some(
                            //   (session) =>
                            //     session?.date === selectedDate &&
                            //     session?.time === formattedTime &&
                            //     session?.id !== sessionId
                            // );

                            // if (isTimeTaken) {
                            //   dispatch(throwError(errMsg));
                            //   setFieldValue('session_time', '');
                            // } else {
                            setFieldValue('session_time', value);
                            // }
                          }}
                          onEndChange={(value) => {
                            setFieldValue('session_end_time', value || '');
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-md-12 col-12 cmb-22">
                    <TextInput
                      label="Session Topic"
                      placeholder="Topic Name"
                      id="session_topic"
                      onChange={(e) => {
                        const id = e.target.id;
                        const value = titleCaseString(e.target.value);
                        setFieldValue(id, value);
                      }}
                      value={values.session_topic}
                      error={errors.session_topic}
                    />
                  </div>
                  <div className="col-md-12 col-12 cmb-22">
                    <TextInput
                      label={
                        values?.speaker_category == '9'
                          ? 'Author Name'
                          : 'Session Name'
                      }
                      placeholder="Name"
                      id="name"
                      onChange={(e) => {
                        const id = e.target.id;
                        const value = titleCaseString(e.target.value);
                        setFieldValue(id, value);
                      }}
                      value={values.name}
                      error={errors.name}
                    />
                  </div>

                  <div className="col-md-12 col-12 cmb-22">
                    <TextArea
                      label="Description"
                      placeholder="Description"
                      rows={3}
                      id="description"
                      onChange={(e) => {
                        const id = e.target.id;
                        const value = titleCaseString(e.target.value);
                        setFieldValue(id, value);
                      }}
                      value={values.description}
                      error={errors.description}
                    />
                  </div>

                  {/* <div className="col-md-12 col-12 cmb-22">
                    <MultipleSelect
                      label="Speaker"
                      placeholder="Select Speakers"
                      id="speaker_id"
                      value={values.speaker_id}
                      error={errors.speaker_id}
                      options={newSpeakerDetails || []}
                      optionKey="id"
                      optionValue="sName"
                      onChange={handleChange}
                    />
                   
                  </div> */}
                  <div className="col-md-12 col-12 cmb-22">
                    <Dropdown
                      label="Speaker Categories"
                      id="speaker_category"
                      options={speakerCategoryOptions}
                      optionKey="id"
                      optionValue="name"
                      onChange={handleChange}
                      placeholder="Select Category"
                      value={values.speaker_category}
                      error={errors.speaker_category}
                    />
                  </div>

                  {(() => {
                    const selectedCategory = speakerCategoryOptions?.find(
                      (item) => item?.id === values?.speaker_category
                    );
                    if (!selectedCategory) return null;
                    const speakerOptions =
                      speakerDetails?.map((elm) => ({
                        ...elm,
                        sName: elm?.name || elm?.new_user_name,
                      })) || [];
                    return (
                      <div className="col-md-12 col-12 cmb-22">
                        {selectedCategory?.type === 'Multiple' ? (
                          <MultipleSelect
                            label="Speaker"
                            placeholder="Select Speakers"
                            id="speaker_id"
                            value={values.speaker_id}
                            error={errors.speaker_id}
                            options={speakerOptions || []}
                            optionKey="id"
                            optionValue="sName"
                            onChange={handleChange}
                          />
                        ) : (
                          <Dropdown
                            label="Speaker"
                            id="speaker_id"
                            options={speakerOptions || []}
                            optionKey="id"
                            optionValue="sName"
                            onChange={handleChange}
                            placeholder="Select Speaker"
                            value={values.speaker_id}
                            error={errors.speaker_id}
                          />
                        )}
                      </div>
                    );
                  })()}

                  <div className="col-md-12 col-12 cmb-22">
                    <Dropdown
                      label="Participation Mode"
                      id="participation_mode"
                      options={participationMode}
                      optionKey="id"
                      optionValue="name"
                      onChange={handleChange}
                      placeholder="Select Mode"
                      value={values.participation_mode}
                      error={errors.participation_mode}
                    />
                  </div>

                  <div className="col-md-12 col-12 cmb-22">
                    <FileUpload
                      label="Transcription"
                      id="transcription"
                      onChange={(e) => {
                        const id = e.target.id;
                        const value = e.target.value;
                        const fileName = e.target.fileName;
                        setFieldValue('transcriptionFileName', fileName);
                        setFieldValue(id, value);
                      }}
                      fileText={getFilenameFromUrl(
                        values?.transcriptionFileName || values.transcription
                      )}
                      error={errors.transcription}
                    />
                  </div>
                  <div className="d-flex justify-content-center gap-4 cmt-30 col-12">
                    <Button
                      text="Cancel"
                      isRounded
                      btnStyle="light-outline"
                      className="cps-40 cpe-40"
                      onClick={onHide}
                    />
                    <Button
                      isRounded
                      text="Done"
                      btnStyle="primary-dark"
                      className="cps-40 cpe-40"
                      btnLoading={btnLoading}
                      disabled={
                        isEqual(values, initialValues) ||
                        Object.keys(errors).length > 0 ||
                        btnLoading
                      }
                      onClick={handleSubmit}
                    />
                  </div>
                </div>
              </form>
            );
          }}
        </Formik>
      </div>
      {/* </Card> */}
    </Modal>
  );
};
export default SessionForm;
